import React from 'react';
import { View, Text, StyleSheet, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { openNavigationToMission } from '../services/pilotService';

export const STATUS_CONFIG = {
  pending:     { color: '#d97706', bg: '#fef3c7', label: 'Pendiente',    icon: 'time-outline' },
  assigned:    { color: '#d97706', bg: '#fef3c7', label: 'Pendiente',    icon: 'time-outline' },
  in_progress: { color: '#7c3aed', bg: '#ede9fe', label: 'En ejecución', icon: 'airplane-outline' },
  completed:   { color: '#16a34a', bg: '#dcfce7', label: 'Completada',   icon: 'checkmark-circle-outline' },
  cancelled:   { color: '#9ca3af', bg: '#f3f4f6', label: 'Cancelada',    icon: 'close-circle-outline' },
};

export const missionTypeIcon = (item) => {
  if (item._type === 'mission_request' && item.mr_type === 'multispectral') return 'leaf-outline';
  if (item._type === 'mission_request') return 'water-outline';
  const objs = (item.objectives || []).map(o => {
    const name = typeof o === 'string' ? o : (o.name || o.description || '');
    return typeof name === 'string' ? name.toLowerCase() : '';
  });
  if (objs.some(o => o.includes('ndvi') || o.includes('multispectral'))) return 'leaf-outline';
  if (objs.some(o => o.includes('thermal') || o.includes('térmico'))) return 'flame-outline';
  if (objs.some(o => o.includes('rgb') || o.includes('mapeo'))) return 'map-outline';
  if (objs.some(o => o.includes('spray') || o.includes('aplicac'))) return 'water-outline';
  return 'airplane-outline';
};

export default function MissionCard({ item, onPress }) {
  const cfg          = STATUS_CONFIG[item.status] || STATUS_CONFIG.assigned;
  const isDrone      = item._type === 'mission_request';
  const isApplication   = isDrone && item.mr_type === 'application';
  const isMultispectral = isDrone && item.mr_type === 'multispectral';
  const blocks   = (item.field_blocks || []).map(b => b.name).join(' · ');
  const products = (item.products || []).map(p => p.trade_name).join(', ');
  const totalHa  = (item.field_blocks || []).reduce((s, b) => s + (parseFloat(b.area) || 0), 0);
  const indices  = (item.objectives_list || []).join(' · ');
  const hasLocation = item.location_polygon || (item.field_blocks || []).some(b => b.polygon);

  const cardId = isApplication
    ? `Orden #${String(item.order_folio).padStart(4, '0')} · Misión #${item._mission_id}`
    : `Misión #${item._mission_id || item.mission_sid || item._raw_id}`;

  const cardBlocks = isApplication
    ? blocks
    : isMultispectral
      ? (indices || item.location_name || '')
      : (item.mission_description || item.mission_name || '');

  return (
    <TouchableOpacity style={styles.card} onPress={onPress} activeOpacity={0.8}>
      <View style={styles.cardTop}>
        <View style={[styles.typeIcon, { backgroundColor: cfg.bg }]}>
          <Ionicons name={missionTypeIcon(item)} size={18} color={cfg.color} />
        </View>
        <View style={{ flex: 1 }}>
          <Text style={styles.cardTitle} numberOfLines={1}>
            {item.location_name || item.enterprise_name || item.mission_name}
          </Text>
          <Text style={styles.cardId}>{cardId}</Text>
          <Text style={styles.cardBlocks} numberOfLines={1}>{cardBlocks}</Text>
        </View>
        <View style={{ alignItems: 'flex-end', gap: 8 }}>
          <View style={[styles.badge, { backgroundColor: cfg.bg }]}>
            <Ionicons name={cfg.icon} size={12} color={cfg.color} style={{ marginRight: 4 }} />
            <Text style={[styles.badgeText, { color: cfg.color }]}>{cfg.label}</Text>
          </View>
          {hasLocation && (
            <TouchableOpacity
              onPress={(e) => { e.stopPropagation?.(); openNavigationToMission(item); }}
              hitSlop={{ top: 6, bottom: 6, left: 12, right: 6 }}
            >
              <Ionicons name="navigate-outline" size={17} color="#9ca3af" />
            </TouchableOpacity>
          )}
        </View>
      </View>

      {isApplication && products ? (
        <Text style={styles.cardProducts} numberOfLines={1}>{products}</Text>
      ) : null}

      <View style={styles.cardFooter}>
        {isApplication && totalHa > 0 && (
          <View style={styles.footerItem}>
            <Ionicons name="map-outline" size={12} color="#9ca3af" />
            <Text style={styles.footerText}>{totalHa.toFixed(1)} ha</Text>
          </View>
        )}
        {isApplication && item.application_date && (
          <View style={styles.footerItem}>
            <Ionicons name="calendar-outline" size={12} color="#9ca3af" />
            <Text style={styles.footerText}>
              {new Date(item.application_date + 'T12:00:00').toLocaleDateString('es-CL', { day: '2-digit', month: 'short' })}
            </Text>
          </View>
        )}
        {isMultispectral && item.scheduled_date && (
          <View style={styles.footerItem}>
            <Ionicons name="calendar-outline" size={12} color="#9ca3af" />
            <Text style={styles.footerText}>
              {new Date(item.scheduled_date + 'T12:00:00').toLocaleDateString('es-CL', { day: '2-digit', month: 'short' })}
            </Text>
          </View>
        )}
        {isApplication && (
          <View style={styles.footerItem}>
            <Ionicons name="water-outline" size={12} color="#9ca3af" />
            <Text style={styles.footerText}>
              {item.rate_type === 'uniform'
                ? (item.dose_per_ha ? `${item.dose_per_ha} ${item.dose_unit}/ha` : 'Tasa uniforme')
                : 'Tasa variable'}
            </Text>
          </View>
        )}
        {item.aircraft?.name && (
          <View style={styles.footerItem}>
            <Ionicons name="airplane-outline" size={12} color="#9ca3af" />
            <Text style={styles.footerText}>{item.aircraft.name}</Text>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  card:         { backgroundColor: '#fff', borderRadius: 14, padding: 14, marginBottom: 8, borderWidth: 1, borderColor: '#f3f4f6' },
  cardTop:      { flexDirection: 'row', alignItems: 'flex-start', gap: 10, marginBottom: 6 },
  typeIcon:     { width: 36, height: 36, borderRadius: 18, justifyContent: 'center', alignItems: 'center', flexShrink: 0 },
  cardTitle:    { fontSize: 14, fontWeight: '700', color: '#111827', marginBottom: 1 },
  cardId:       { fontSize: 11, color: '#9ca3af', marginBottom: 2 },
  cardBlocks:   { fontSize: 12, color: '#6b7280' },
  badge:        { flexDirection: 'row', alignItems: 'center', paddingHorizontal: 8, paddingVertical: 3, borderRadius: 20 },
  badgeText:    { fontSize: 11, fontWeight: '600' },
  cardProducts: { fontSize: 12, color: '#6b7280', marginBottom: 8 },
  cardFooter:   { flexDirection: 'row', flexWrap: 'wrap', gap: 10, borderTopWidth: 1, borderTopColor: '#f9fafb', paddingTop: 10 },
  footerItem:   { flexDirection: 'row', alignItems: 'center', gap: 4 },
  footerText:   { fontSize: 12, color: '#6b7280' },
});
